import { useEffect, useMemo, useState } from 'react'
import { MOBILE_STORE_URLS } from '@numeo/types-core'
import Alert from '@mui/joy/Alert'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Card from '@mui/joy/Card'
import Chip from '@mui/joy/Chip'
import CircularProgress from '@mui/joy/CircularProgress'
import FormControl from '@mui/joy/FormControl'
import FormHelperText from '@mui/joy/FormHelperText'
import FormLabel from '@mui/joy/FormLabel'
import Input from '@mui/joy/Input'
import Option from '@mui/joy/Option'
import Select from '@mui/joy/Select'
import Snackbar from '@mui/joy/Snackbar'
import Stack from '@mui/joy/Stack'
import Textarea from '@mui/joy/Textarea'
import Typography from '@mui/joy/Typography'
import { mobileVersionPolicyApi, MobilePlatform, MobileVariant, UpdateType, type MobileVersionPolicy } from 'api/mobileVersionPolicy'

// Local-only preview states layered on top of UpdateType — `pending` while the
// preview request is in flight, `error` when the request fails. Kept here (not
// in the shared enum) because the backend never emits these.
type PreviewState = UpdateType | 'pending' | 'error'

const VARIANTS: { value: MobileVariant; label: string }[] = [
    { value: MobileVariant.Dispatcher, label: 'Dispatcher (SpotFinder)' },
    { value: MobileVariant.Driver, label: 'Driver (Numeo Driver)' },
]

const PLATFORMS: { value: MobilePlatform; label: string }[] = [
    { value: MobilePlatform.Ios, label: 'iOS' },
    { value: MobilePlatform.Android, label: 'Android' },
]

const SEMVER_RE = /^\d+\.\d+\.\d+$/
const URL_RE = /^https?:\/\//

type FormDraft = MobileVersionPolicy & { _exists: boolean }

const emptyDraft = (): FormDraft => ({
    minimumVersion: '',
    recommendedVersion: '',
    storeUrl: '',
    releaseNotes: '',
    _exists: false,
})

function policyToDraft(p?: MobileVersionPolicy): FormDraft {
    if (!p) return emptyDraft()
    return {
        minimumVersion: p.minimumVersion,
        recommendedVersion: p.recommendedVersion,
        storeUrl: p.storeUrl,
        releaseNotes: p.releaseNotes ?? '',
        _exists: true,
    }
}

function validateDraft(d: FormDraft): string | null {
    if (!SEMVER_RE.test(d.minimumVersion)) return 'Minimum version must be x.y.z'
    if (!SEMVER_RE.test(d.recommendedVersion)) return 'Recommended version must be x.y.z'
    if (!URL_RE.test(d.storeUrl)) return 'Store URL must start with http:// or https://'
    return null
}

interface SnackbarState {
    open: boolean
    message: string
    color: 'success' | 'danger'
}

const draftKey = (v: MobileVariant, p: MobilePlatform) => `${v}:${p}`

export default function MobileVersionPolicyPage() {
    const [variant, setVariant] = useState<MobileVariant>(MobileVariant.Dispatcher)
    const [drafts, setDrafts] = useState<Record<string, FormDraft>>({})
    const [loading, setLoading] = useState(true)
    const [busy, setBusy] = useState<string | null>(null)
    const [previewVersion, setPreviewVersion] = useState('2.0.1')
    const [previews, setPreviews] = useState<Record<string, PreviewState>>({})
    const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', color: 'success' })

    useEffect(() => {
        const loadConfig = async () => {
            setLoading(true)
            try {
                const next = await mobileVersionPolicyApi.getAll()
                const d: Record<string, FormDraft> = {}
                for (const v of VARIANTS) {
                    for (const p of PLATFORMS) {
                        d[draftKey(v.value, p.value)] = policyToDraft(next[v.value]?.[p.value])
                    }
                }
                setDrafts(d)
            } catch {
                setSnackbar({ open: true, message: 'Failed to load mobile version policy', color: 'danger' })
            } finally {
                setLoading(false)
            }
        }
        void loadConfig()
    }, [])

    function updateDraft(platform: MobilePlatform, patch: Partial<FormDraft>) {
        setDrafts((prev) => ({
            ...prev,
            [draftKey(variant, platform)]: { ...prev[draftKey(variant, platform)], ...patch },
        }))
    }

    async function save(platform: MobilePlatform) {
        const key = draftKey(variant, platform)
        const draft = drafts[key]
        const err = validateDraft(draft)
        if (err) {
            setSnackbar({ open: true, message: err, color: 'danger' })
            return
        }
        setBusy(key)
        try {
            const policy: MobileVersionPolicy = {
                minimumVersion: draft.minimumVersion,
                recommendedVersion: draft.recommendedVersion,
                storeUrl: draft.storeUrl,
                ...(draft.releaseNotes && draft.releaseNotes.length > 0 ? { releaseNotes: draft.releaseNotes } : {}),
            }
            await mobileVersionPolicyApi.setPolicy(variant, platform, policy)
            setDrafts((prev) => ({ ...prev, [key]: { ...prev[key], _exists: true } }))
            setSnackbar({ open: true, message: `Saved ${variant} / ${platform}`, color: 'success' })
            // Refresh preview for this row.
            void runPreview(platform)
        } catch (e) {
            const msg = e instanceof Error ? e.message : 'Save failed'
            setSnackbar({ open: true, message: msg, color: 'danger' })
        } finally {
            setBusy(null)
        }
    }

    async function clear(platform: MobilePlatform) {
        const key = draftKey(variant, platform)
        if (!window.confirm(`Clear ${variant} / ${platform} policy? Mobile clients will fall back to "no update".`)) return
        setBusy(key)
        try {
            await mobileVersionPolicyApi.clearPolicy(variant, platform)
            setDrafts((prev) => ({ ...prev, [key]: emptyDraft() }))
            setPreviews((prev) => ({ ...prev, [key]: UpdateType.None }))
            setSnackbar({ open: true, message: `Cleared ${variant} / ${platform}`, color: 'success' })
        } catch {
            setSnackbar({ open: true, message: 'Clear failed', color: 'danger' })
        } finally {
            setBusy(null)
        }
    }

    async function runPreview(platform: MobilePlatform) {
        if (!SEMVER_RE.test(previewVersion)) return
        const key = draftKey(variant, platform)
        setPreviews((prev) => ({ ...prev, [key]: 'pending' }))
        try {
            const r = await mobileVersionPolicyApi.preview(variant, platform, previewVersion)
            setPreviews((prev) => ({ ...prev, [key]: r.updateType }))
        } catch {
            setPreviews((prev) => ({ ...prev, [key]: 'error' }))
        }
    }

    async function refreshPreviews() {
        await Promise.all(PLATFORMS.map((p) => runPreview(p.value)))
    }

    const previewVersionValid = SEMVER_RE.test(previewVersion)

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box sx={{ p: 3, maxWidth: 1100 }}>
            <Typography level="h3" sx={{ mb: 1 }}>
                Mobile Version Policy
            </Typography>
            <Typography level="body-sm" sx={{ mb: 3 }}>
                Controls hard-update (force users to the store) and soft-update (dismissible prompt) behavior for the dispatcher and driver mobile apps. Changes
                take effect on the next app launch / foreground.
            </Typography>

            <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
                <Stack direction="row" spacing={2} alignItems="flex-end" flexWrap="wrap" useFlexGap>
                    <FormControl sx={{ minWidth: 240 }}>
                        <FormLabel>Variant</FormLabel>
                        <Select value={variant} onChange={(_, v) => v && setVariant(v)}>
                            {VARIANTS.map((v) => (
                                <Option key={v.value} value={v.value}>
                                    {v.label}
                                </Option>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl sx={{ minWidth: 200 }}>
                        <FormLabel>Preview installed version</FormLabel>
                        <Input value={previewVersion} onChange={(e) => setPreviewVersion(e.target.value)} placeholder="2.0.1" />
                        <FormHelperText>{previewVersionValid ? 'Valid semver' : 'Format x.y.z'}</FormHelperText>
                    </FormControl>
                    <Button variant="outlined" onClick={refreshPreviews} disabled={!previewVersionValid}>
                        Run preview
                    </Button>
                </Stack>
            </Card>

            <Stack spacing={3}>
                {PLATFORMS.map((p) => (
                    <PlatformCard
                        key={p.value}
                        variant={variant}
                        platform={p.value}
                        platformLabel={p.label}
                        draft={drafts[draftKey(variant, p.value)] ?? emptyDraft()}
                        onChange={(patch) => updateDraft(p.value, patch)}
                        busy={busy === draftKey(variant, p.value)}
                        onSave={() => save(p.value)}
                        onClear={() => clear(p.value)}
                        previewType={previews[draftKey(variant, p.value)]}
                        previewVersion={previewVersion}
                    />
                ))}
            </Stack>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3500}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                color={snackbar.color}
                variant="soft"
            >
                {snackbar.message}
            </Snackbar>
        </Box>
    )
}

interface PlatformCardProps {
    variant: MobileVariant
    platform: MobilePlatform
    platformLabel: string
    draft: FormDraft
    onChange: (patch: Partial<FormDraft>) => void
    busy: boolean
    onSave: () => void
    onClear: () => void
    previewType?: PreviewState
    previewVersion: string
}

function PlatformCard({ variant, platform, platformLabel, draft, onChange, busy, onSave, onClear, previewType, previewVersion }: PlatformCardProps) {
    const validation = useMemo(() => validateDraft(draft), [draft])
    const dirtyOrEmpty = !draft.minimumVersion && !draft.recommendedVersion && !draft.storeUrl

    const previewChip = (() => {
        if (!previewType) return null
        if (previewType === 'pending') return <Chip color="neutral">Checking…</Chip>
        if (previewType === 'error') return <Chip color="warning">Preview failed</Chip>
        if (previewType === UpdateType.Hard)
            return (
                <Chip color="danger" variant="solid">
                    HARD — would force-update {previewVersion}
                </Chip>
            )
        if (previewType === UpdateType.Soft)
            return (
                <Chip color="warning" variant="solid">
                    SOFT — would prompt at {previewVersion}
                </Chip>
            )
        return (
            <Chip color="success" variant="solid">
                NONE — {previewVersion} is current
            </Chip>
        )
    })()

    return (
        <Card variant="outlined" sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <Typography level="title-lg">{platformLabel}</Typography>
                {draft._exists ? <Chip color="primary">Active policy</Chip> : <Chip color="neutral">No policy (fail-open)</Chip>}
                <Box sx={{ flex: 1 }} />
                {previewChip}
            </Stack>

            <Stack spacing={2}>
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                    <FormControl sx={{ minWidth: 220, flex: 1 }} error={Boolean(draft.minimumVersion) && !SEMVER_RE.test(draft.minimumVersion)}>
                        <FormLabel>Minimum version (hard threshold)</FormLabel>
                        <Input
                            value={draft.minimumVersion}
                            onChange={(e) => onChange({ minimumVersion: e.target.value })}
                            placeholder="2.0.0"
                            disabled={busy}
                        />
                        <FormHelperText>Below this version, the app is hard-blocked.</FormHelperText>
                    </FormControl>

                    <FormControl sx={{ minWidth: 220, flex: 1 }} error={Boolean(draft.recommendedVersion) && !SEMVER_RE.test(draft.recommendedVersion)}>
                        <FormLabel>Recommended version (soft threshold)</FormLabel>
                        <Input
                            value={draft.recommendedVersion}
                            onChange={(e) => onChange({ recommendedVersion: e.target.value })}
                            placeholder="2.1.0"
                            disabled={busy}
                        />
                        <FormHelperText>Below this (and ≥ minimum), users see a dismissible prompt.</FormHelperText>
                    </FormControl>
                </Stack>

                <FormControl error={Boolean(draft.storeUrl) && !URL_RE.test(draft.storeUrl)}>
                    <FormLabel>Store URL</FormLabel>
                    <Input
                        value={draft.storeUrl}
                        onChange={(e) => onChange({ storeUrl: e.target.value })}
                        placeholder={
                            platform === MobilePlatform.Ios
                                ? `${MOBILE_STORE_URLS.testFlight}  (beta)  —  ${MOBILE_STORE_URLS.appStore}  (prod)`
                                : MOBILE_STORE_URLS.playStore
                        }
                        disabled={busy}
                    />
                    <FormHelperText>
                        Where the &quot;Update&quot; button takes the user.
                        {platform === MobilePlatform.Ios
                            ? ` For staging / TestFlight users, use ${MOBILE_STORE_URLS.testFlight}. For production, use the App Store URL.`
                            : ''}
                    </FormHelperText>
                </FormControl>

                <FormControl>
                    <FormLabel>Release notes (optional)</FormLabel>
                    <Textarea
                        minRows={2}
                        value={draft.releaseNotes ?? ''}
                        onChange={(e) => onChange({ releaseNotes: e.target.value })}
                        placeholder="• Faster load board&#10;• Bug fixes"
                        disabled={busy}
                    />
                    <FormHelperText>Shown inside the soft-update modal.</FormHelperText>
                </FormControl>

                {validation && !dirtyOrEmpty ? <Alert color="warning">{validation}</Alert> : null}

                <Stack direction="row" spacing={2}>
                    <Button onClick={onSave} disabled={busy || Boolean(validation)} loading={busy}>
                        {draft._exists ? 'Save changes' : 'Publish policy'}
                    </Button>
                    {draft._exists ? (
                        <Button color="danger" variant="soft" onClick={onClear} disabled={busy}>
                            Clear policy
                        </Button>
                    ) : null}
                </Stack>
            </Stack>

            <Typography level="body-xs" sx={{ mt: 2, color: 'text.tertiary' }}>
                Path:{' '}
                <code>
                    numeo_config.mobileVersions.{variant}.{platform}
                </code>
            </Typography>
        </Card>
    )
}
